import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Button from '../../../components/Button';
import { audioGuidesService } from '../../../services/audioGuidesService';
import type { AudioGuide, CreateAudioGuideData } from '../../../types/audio-guides';

interface AudioGuideModalProps {
    audioGuide: AudioGuide | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export const AudioGuideModal = ({
    audioGuide,
    isOpen,
    onClose,
    onSave,
}: AudioGuideModalProps) => {
    const isNew = !audioGuide;

    // Form state
    const [title, setTitle] = useState('');
    const [bannerText, setBannerText] = useState('');
    const [subText, setSubText] = useState('');
    const [audioUrl, setAudioUrl] = useState('');
    const [bannerPath, setBannerPath] = useState('');
    const [status, setStatus] = useState<'draft' | 'published'>('draft');
    const [isFree, setIsFree] = useState(false);
    const [order, setOrder] = useState(0);
    const [aiGuidance, setAiGuidance] = useState('');

    // UI state
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Initialize form with existing guide data
    useEffect(() => {
        const initializeForm = async () => {
            if (audioGuide) {
                setTitle(audioGuide.title);
                setBannerText(audioGuide.bannerText);
                setSubText(audioGuide.subText);
                setAudioUrl(audioGuide.audio_path);
                setBannerPath(audioGuide.bannerPath);
                setStatus(audioGuide.status || 'draft');
                setIsFree(audioGuide.isFree || false);
                setOrder(audioGuide.order || 0);
                setAiGuidance(audioGuide.aiGuidance || '');
            } else {
                // Reset form for new guide
                setTitle('');
                setBannerText('');
                setSubText('');
                setAudioUrl('');
                setBannerPath('');
                setStatus('draft');
                setIsFree(false);
                setOrder(0);
                setAiGuidance('');
            }
            setError(null);
        };

        initializeForm();
    }, [audioGuide]);

    // Validate URL format
    const isValidUrl = (url: string): boolean => {
        if (!url) return false;
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validation
        if (!title.trim()) {
            setError('Title is required');
            return;
        }

        if (!bannerText.trim()) {
            setError('Banner Text is required');
            return;
        }

        if (!subText.trim()) {
            setError('Sub Text is required');
            return;
        }

        if (!audioUrl.trim()) {
            setError('Audio URL is required');
            return;
        }

        if (!isValidUrl(audioUrl)) {
            setError('Please enter a valid URL for the audio file');
            return;
        }

        if (!bannerPath.trim()) {
            setError('Banner image is required');
            return;
        }

        try {
            setIsSaving(true);

            if (isNew) {
                // Create new guide
                const createData: CreateAudioGuideData = {
                    title: title.trim(),
                    bannerText: bannerText.trim(),
                    subText: subText.trim(),
                    audio_path: audioUrl.trim(),
                    bannerPath: bannerPath.trim(),
                    type: 'audio',
                    status,
                    isFree,
                    order,
                    aiGuidance: aiGuidance.trim(),
                };
                await audioGuidesService.createAudioGuide(createData);
            } else {
                // Update existing guide
                await audioGuidesService.updateAudioGuide(audioGuide.uid, {
                    title: title.trim(),
                    bannerText: bannerText.trim(),
                    subText: subText.trim(),
                    audio_path: audioUrl.trim(),
                    bannerPath: bannerPath.trim(),
                    type: 'audio',
                    status,
                    isFree,
                    order,
                    aiGuidance: aiGuidance.trim(),
                });
            }

            // Success - close modal and refresh list
            onSave();
            onClose();
        } catch (err) {
            console.error('Failed to save audio guide:', err);
            setError(err instanceof Error ? err.message : 'Failed to save audio guide');
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 my-8">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isNew ? 'Add New Audio Guide' : 'Edit Audio Guide'}
                    </h3>
                    <Button
                        onClick={onClose}
                        variant="ghost"
                        size="sm"
                        icon={<X size={20} />}
                        disabled={isSaving}
                    />
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                            {error}
                        </div>
                    )}

                    {/* Section 1: Basic Information */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">Basic Information</h4>

                        {/* Title */}
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                                Title *
                            </label>
                            <input
                                type="text"
                                id="title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., Morning Wake Up"
                                required
                            />
                        </div>

                        {/* Banner Text */}
                        <div>
                            <label htmlFor="bannerText" className="block text-sm font-medium text-gray-700 mb-1">
                                Banner Text *
                            </label>
                            <input
                                type="text"
                                id="bannerText"
                                value={bannerText}
                                onChange={(e) => setBannerText(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Short text for banner display"
                                required
                            />
                        </div>
                    </div>

                    {/* Section 2: Content */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">Content</h4>

                        {/* Sub Text */}
                        <div>
                            <label htmlFor="subText" className="block text-sm font-medium text-gray-700 mb-1">
                                Sub Text *
                            </label>
                            <textarea
                                id="subText"
                                value={subText}
                                onChange={(e) => setSubText(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Detailed description or subtitle"
                                rows={3}
                                required
                            />
                        </div>

                        {/* Audio URL */}
                        <div>
                            <label htmlFor="audioUrl" className="block text-sm font-medium text-gray-700 mb-1">
                                Audio URL *
                            </label>
                            <input
                                type="url"
                                id="audioUrl"
                                value={audioUrl}
                                onChange={(e) => setAudioUrl(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="https://example.com/audio.mp3"
                                required
                            />
                        </div>

                        {/* Banner Image */}
                        <input
                            type="url"
                            id="bannerPath"
                            value={bannerPath}
                            onChange={(e) => setBannerPath(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="https://example.com/banner.jpg"
                            required
                        />

                        {/* AI Guidance */}
                        <div>
                            <label htmlFor="aiGuidance" className="block text-sm font-medium text-gray-700 mb-1">
                                AI Guidance
                            </label>
                            <textarea
                                id="aiGuidance"
                                value={aiGuidance}
                                onChange={(e) => setAiGuidance(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Guidance for the AI on when and how to recommend this audio guide..."
                                rows={4}
                            />
                        </div>
                    </div>

                    {/* Section 3: Settings */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">Settings</h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Status */}
                            <div>
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                                    Status
                                </label>
                                <select
                                    id="status"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value as 'draft' | 'published')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="draft">Draft</option>
                                    <option value="published">Published</option>
                                </select>
                            </div>

                            {/* Order */}
                            <div>
                                <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-1">
                                    Order
                                </label>
                                <input
                                    type="number"
                                    id="order"
                                    value={order}
                                    onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Is Free */}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="isFree"
                                checked={isFree}
                                onChange={(e) => setIsFree(e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="isFree" className="ml-2 block text-sm text-gray-700">
                                Free (accessible without premium subscription)
                            </label>
                        </div>
                    </div>
                </form>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : isNew ? 'Create Audio Guide' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
